import { ParsedUrlQuery } from 'querystring';

import React, {
  useRef,
  useState,
  useCallback,
  MutableRefObject,
  useEffect,
  useMemo,
} from 'react';

import { useMutation } from '@tanstack/react-query';
import { useRouter, useSearchParams } from 'next/navigation';
import { Element } from 'react-scroll';
import { isArray } from 'lodash-es';
import dynamic from 'next/dynamic';
import moment from 'moment';
import { parseDate } from '@datepicker-react/hooks';
import { setCookie } from 'cookies-next';

import { useSearchBarContext } from '@contexts/hotel/SearchBar';
import GalleryHeroImages from '@components/galleries/GalleryHeroImages/GalleryHeroImages';
import Breadcrumb from '@components/Breadcrumb/Breadcrumb';
import { Separator, Layout } from '@components/formStyles';
import {
  BreadcrumbWrapper,
  ImagesWrapper,
  Wrapper,
  Container,
  BreadcrumbDesktop,
  HotelLocationWrapper,
  BasketErrorText,
  PaymentStartedErrorContainer,
  LinkText,
  SeoTextContainer,
} from '@components/views/hotel/detail/HotelDetailStyle';
import { formatArrayToString } from '@utils/utils';
import {
  LoyaltyType,
  RoomViewModel,
  ApiError,
  PackageDetailViewModel,
  PackageTransferViewModel,
  RoomLowestPriceViewModel,
  HotelDetailViewModel,
  PackagePrebookAddRequestViewModel,
  FlightNodeViewModel,
} from '@api/v3';
import {
  cloudineryImage,
  getTypedFeatureProps,
} from '@views/hotel/detail/utils/features';
import { useAuthState } from '@contexts/auth/AuthContext';
import BankInstallments from '@components/ui/bank-installments/BankInstallments';
import ErrorPage from '@components/views/error/ErrorPage';
import { addHotelLetUsCallYouSuccessEventToGTM } from '@utils/gtm/hotel-gtm';
import useUnlockBasket from '@components/views/hooks/useUnlockBasket';
import {
  useBasketId,
  useIsKocAilemValidated,
  getBasketFromCache,
} from '@hooks/cache';
import { BreadCrumbListSchemaOrg } from '@utils/schemaorg/BreadCrumbListSchemaOrg';
import ScrollToTop from '@components/ScrollToTop/ScrollToTop';
import { callService } from '@api/remote-call';
import { Currencies } from '@constants/constants';
import { formatMomentDate, GTM_DATE_FORMAT } from '@utils/dateUtil';
import { useDevice } from '@contexts/device';
import { useOnce } from '@utils/gtm/hooks/useOnce';
import { addNoAvailability } from '@utils/gtm/events-gtm';
import { isClientSide } from '@utils/env';
import SeoText from '@components/views/landing/components/seoText/seoText';
import {
  useHideChatGPT,
  useSetChatGPTMessage,
} from '@components/ChatGPTLoader/hooks';
import { getDetailChatGPTMessage } from '@components/ChatGPTLoader/utils';
import { useOverrideLinkHref } from '@hooks/useOverrideLinkHref';
import { ModalView } from '@components/views/hotel/detail/components/details-galleries-modal';
import {
  useHotelDetailState,
  useSetModalLoyaltyType,
  useSetSelectedVariations,
} from '@components/views/hotel/detail/context';
import { PriceDetailsDrawerItem } from '@components/views/hotel/components/PriceDetails';
import { formatHotelImages } from '@components/views/hotel/detail/utils/formats';
import BannerDetail, {
  BannerDetailCarousel,
} from '@components/views/hotel/listing/components/Banner/BannerDetail';
import { BannerDetailWrapper } from '@components/views/hotel/listing/components/Banner/BannerStyle';
import {
  getOnlyAvailableRoomVariation,
  sortRoomVariationItems,
  getRoomsGalleryCategory as getRoomsGalleryCategoryHotel,
} from '@components/views/hotel/detail/helpers';
import HotelFeatures from '@components/views/hotel/detail/components/hotel-features';
import { RoomsVariationBarItem } from '@components/views/hotel/detail/components/rooms-variation-bar/Item/props';
import CyprusTab from '@components/views/package/detail/components/package-hotel-selection-tab';
import { HotelDetailSchemaOrg } from '@utils/schemaorg/HotelDetailSchemaOrg';
import CustomIcon from '@components/utils/CustomIcon';
import Typography from '@components/Typography';
import { InfoIcon } from '@assets/icons';
import { getFlightPrebookTravelersFromGuestInfo } from '@components/views/flight/utils';
import { cleanArray } from '@utils/cleanArray';
import { useToggle } from '@hooks/common';
import useBasketInformation, {
  useBasketMutation,
} from '@hooks/useBasketInformation';
import { packageDetailAndAddtoCart } from '@utils/newDataLayer/productDetailAndAddtoCart';
import useGetasPath from '@hooks/useGetasPath';
import { paramsToObject } from '@components/views/utils';
import { isEmptyBasket } from '@components/views/reservation-information/utils';
import { generateUUID } from '@utils/generateUUID';
import { SessionScopeGTM } from '@utils/gtm/session-scope';

import PackageSearchBar from './components/package-search-bar';
import useCatchTrivagoUser from '../../../../integrations/trivago/useCatchTrivagoUser';
import {
  GalleryCategory,
  HotelRooms,
  ModalViewInfo,
  PAYMENT_STARTED,
} from './types';
import {
  extractItems,
  getAvailableRoom,
  getGuestInformationFromRooms,
  getLowestVariation,
  getRoomsGalleryCategory,
  groupImagesByCategory,
  prepareTransferForPrebookAdd,
} from './helpers';
import RoomOptions from './components/room-option';
import {
  CyprusTabWrapper,
  Text,
  ErrorModalBody,
  ErrorModalButton,
} from './styled';
import { PackageResponsiveSeachBar } from './components/package-responsive-search-bar';
import PackageSummaryCard from './package-summary-card';
import { FlightProps } from '../components/helper';
import CurrentCartProductsModal from './components/CurrentCartProductsModal';

const ResearchWrapper = dynamic(() =>
  import('../listing/styled').then(({ ResearchWrapper }) => ResearchWrapper)
);

const ResearchContainer = dynamic(() =>
  import('../listing/styled').then(({ ResearchContainer }) => ResearchContainer)
);

const ErrorInfoModal = dynamic(
  () => import('@components/views/components/ErrorInfoModal/ErrorInfoModal')
);

const FlightTransferSelectionComponent = dynamic(
  () =>
    import(
      '@components/views/package/components/FlightTransferSelectionComponent'
    )
);

const ConfirmationModal = dynamic(
  () => import('@components/ui/modals/ConfirmationModal')
);

const LoyaltyVerifications = dynamic(
  () => import('@components/ui/loyalty-verifications/LoyaltyVerifications')
);

const ErrorModal = dynamic(() => import('@components/ui/modals/ErrorModal'));

const RoomWarningInfo = dynamic(
  () => import('@components/views/hotel/detail/components/room-warning')
);
const HotelLocation = dynamic(
  () =>
    import(
      '@components/views/hotel/detail/components/hotel-location/HotelLocation'
    )
);

const Announcements = dynamic(
  () =>
    import(
      '@components/views/hotel/detail/components/announcements/Announcements'
    )
);

const Map = dynamic(() => import('@components/ui/map/Map'));

const Modal = dynamic(() => import('@components/Modal/next/Modal'));

const GeneralLoader = dynamic(() => import('@components/ui/GeneralLoader'));
const AboutUs = dynamic(
  () => import('@components/views/hotel/detail/components/about-us/AboutUs')
);
const DetailsAndGalleries = dynamic(() =>
  import(
    '@components/views/hotel/detail/components/details-galleries-modal'
  ).then((module) => module.default)
);
const PriceDetails = dynamic(() =>
  import('@components/views/hotel/components/PriceDetails').then(
    (module) => module.default
  )
);
const Rules = dynamic(
  () =>
    import('@components/views/hotel/detail/components/hotelDetailRules/Rules')
);
const HotelDetailSkeleton = dynamic(
  () => import('@components/views/hotel/detail/components/skeleton/Skeleton')
);

const PackageDetail = ({
  selectedTab,
  setSelectedTab,
  packageData,
  refetchPackage,
  isGetPackageDetailError,
  isPackageLoading,
  isPackageFetching,
  showFetchingAnimation,
  setShowFetchingAnimation,
  shouldResearchPackage,
  setShouldResearchPackage,
  onlyHotelData,
  timers,
  showErrorModalPackageTimer,
  backToDetail,
}: {
  selectedTab?: string;
  setSelectedTab?: (string) => void;
  packageData: PackageDetailViewModel;
  refetchPackage: any;
  isGetPackageDetailError: boolean;
  isPackageFetching: boolean;
  isPackageLoading: boolean;
  showFetchingAnimation: boolean;
  setShowFetchingAnimation: (data: boolean) => void;
  shouldResearchPackage: boolean;
  setShouldResearchPackage: (data: boolean) => void;
  onlyHotelData?: HotelDetailViewModel;
  metaInfo?: Record<string, string>;
  timers: any;
  showErrorModalPackageTimer: boolean;
  backToDetail: () => void;
}) => {
  const { push } = useRouter();
  const asPath = useGetasPath();
  const searchParams = useSearchParams();
  const query: ParsedUrlQuery = useMemo(() => {
    return paramsToObject(searchParams?.entries());
  }, [searchParams]);

  const { userInfo } = useAuthState();
  const { basketInfo } = useBasketInformation();

  const {
    startDate: inDate,
    endDate: outDate,
    hotelRoomsList: hotelRoomList,
  } = useSearchBarContext();

  const toggleDateSelectionModal = () => {
    setIsAirportSelectionClicked(true);
    setIsAirportDateSelectionStarted(true);
  };

  const setModalLoyaltyType = useSetModalLoyaltyType();
  const [hotelRooms, setHotelRooms] = useState<HotelRooms[]>([]);
  const { mutate: basketMutate, data: basketMutationData } =
    useBasketMutation();
  const [currentCartProductsModal, setCurrentCartProductsModal] =
    useState(false);
  const [isAirportSelectionClicked, setIsAirportSelectionClicked] =
    useState(false);
  const [isAirportDateSelectionStarted, setIsAirportDateSelectionStarted] =
    useState(false);

  const showLoader = showFetchingAnimation
    ? isPackageFetching
      ? true
      : false
    : isPackageFetching && !packageData
    ? true
    : false;

  SessionScopeGTM('Detay Sayfası', 'DP Kıbrıs Otel');
  const { in: startDate, out: endDate, room = ['2'] } = query;
  const { isDesktop, isMobile } = useDevice();
  const { loyaltyType, selectedVariations, unSelectedRooms } =
    useHotelDetailState();
  const setSelectedVariations = useSetSelectedVariations();
  const roomsRef = useRef<HTMLElement>();
  const featuresRef = useRef<HTMLElement>();
  const aboutsRef = useRef<HTMLElement>();
  const announcementsRef = useRef<HTMLElement>();
  const scoringRef = useRef<HTMLElement>();
  const [confirmPackageModal, toggleConfirmModal, setConfirmModal] =
    useToggle();
  const [showErrorModalDeletePackage, setShowErrorModalDeletePackage] =
    useState(false);

  const [showInstallments, setShowInstallments] = useState<boolean>(false);

  const [activeGalleryModalName, setActiveGalleryModalName] =
    useState<string>('');

  const [updatedLowestPrice, setUpdatedLowestPrice] =
    useState<RoomLowestPriceViewModel>();
  const [selectedDepatureFlight, setSelectedDepatureFlight] =
    useState<FlightProps>();
  const [selectedReturnFlight, setSelectedReturnFlight] =
    useState<FlightProps>();
  const [selectedDepatureTransfer, setSelectedDepatureTransfer] =
    useState<PackageTransferViewModel>();
  const [selectedReturnTransfer, setSelectedReturnTransfer] =
    useState<PackageTransferViewModel>();

  const { adultCount, childCount } =
    getGuestInformationFromRooms(hotelRoomList);

  const [errorModalStatusMessage, setErrorModalStatusMessage] = useState({
    title: '',
    description: '',
  });

  const isValidatedKocAilem = useIsKocAilemValidated();

  useCatchTrivagoUser(asPath);
  const basketId = useBasketId();
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [showMap, setShowMap] = useState(false);
  const [modalViewInfo, setGalleryDetailsModalViewInfo] =
    useState<ModalViewInfo>({
      galleriesDetailsModalShown: false,
      view: ModalView.NONE,
      entityType: 'hotel',
    });

  const [viewedRoom, setViewedRoom] = useState<RoomViewModel | undefined>(
    undefined
  );
  const [isLetUsCallYouModalVisible, setIsLetUsCallYouModalVisible] =
    useState(false);
  const [hotelGalleries, setHotelGalleries] = useState<
    ReturnType<typeof groupImagesByCategory>
  >({});
  const [disable, setDisable] = useState(false);
  const showKocAilem = useMemo(() => {
    return isValidatedKocAilem ? false : query?.utm_source === 'kocailem';
  }, []);

  // ===============================
  // Handle view room details
  // ===============================
  const viewRoom = useCallback((room: RoomViewModel, view: ModalView) => {
    setActiveGalleryModalName('Oda Fotografları');
    setViewedRoom(room);
    setGalleryDetailsModalViewInfo({
      galleriesDetailsModalShown: true,
      view,
      entityType: 'room',
    });
  }, []);
  useEffect(() => {
    showKocAilem && setModalLoyaltyType(LoyaltyType.KOC_AILEM);
  }, []);

  const hotelData = packageData?.hotel;
  const transferData = packageData?.transfers;
  const flightData = packageData?.flights;

  useEffect(() => {
    if (hotelData?.lowestPrice) {
      setUpdatedLowestPrice(hotelData?.lowestPrice);
    }
  }, [hotelData]);

  const totalTransferPrice = useMemo(() => {
    return (
      (selectedDepatureTransfer?.trySalePrice || 0) +
      (selectedReturnTransfer?.trySalePrice || 0)
    );
  }, [
    selectedDepatureTransfer,
    selectedReturnTransfer,
    adultCount,
    childCount,
  ]);

  useEffect(() => {
    setUpdatedLowestPrice((prevState) => ({
      ...prevState,
      price: hotelData?.lowestPrice?.price
        ? (hotelData?.lowestPrice?.price || 0) +
          (selectedDepatureFlight?.orginalItem?.totalPrice || 0) +
          (selectedReturnFlight?.orginalItem?.totalPrice || 0) +
          totalTransferPrice
        : undefined,
      originalPrice: hotelData?.lowestPrice?.originalPrice
        ? (hotelData?.lowestPrice?.originalPrice || 0) +
          (selectedDepatureFlight?.orginalItem?.totalPrice || 0) +
          (selectedReturnFlight?.orginalItem?.totalPrice || 0) +
          totalTransferPrice
        : undefined,
      originalDiscountedPrice: hotelData?.lowestPrice?.originalDiscountedPrice
        ? (hotelData?.lowestPrice?.originalDiscountedPrice || 0) +
          (selectedDepatureFlight?.orginalItem?.totalPrice || 0) +
          (selectedReturnFlight?.orginalItem?.totalPrice || 0) +
          totalTransferPrice
        : undefined,
    }));
  }, [
    selectedDepatureFlight,
    selectedDepatureTransfer,
    selectedReturnFlight,
    selectedReturnTransfer,
  ]);

  useHideChatGPT(Boolean(hotelData?.isExpedia));
  useSetChatGPTMessage(getDetailChatGPTMessage(hotelData?.name ?? ''));
  useOverrideLinkHref({
    elementId: 'insider-form-banner',
    queryParams: { name: hotelData?.name, id: hotelData?.id },
  });

  useEffect(() => {
    setHotelGalleries(
      groupImagesByCategory(
        hotelData?.images ?? [],
        'hotelImageCategoryName',
        hotelData?.name || 'Setur',
        hotelData?.rooms
      )
    );
  }, [hotelData?.images]);

  useOnce(() => {
    if (!hotelData?.rooms || hotelData?.rooms.length === 0) {
      addNoAvailability(hotelData?.name, `${startDate} - ${endDate}`);
    }
  });

  useEffect(() => {
    if (!hotelData || !hotelData?.rooms || !hotelData?.rooms?.length) return;

    const totalImagesCount =
      hotelData?.rooms?.reduce((total, room) => {
        return total + (room?.images?.length || 0);
      }, 0) || 0;
    if (totalImagesCount === 0) return;

    const roomsCategory: GalleryCategory[] = [];

    hotelData?.rooms?.forEach((room) =>
      room.images.forEach((roomImages) =>
        roomsCategory.push({
          url: roomImages?.url ?? '/static/images/setur-static.jpg',
          hotelImageCategoryName: 'Odalar',
          original:
            cloudineryImage(roomImages?.url, '420', '250') ??
            '/static/images/setur-static.jpg',
          originalAlt: room?.name ?? 'Setur Görseli Hazırlanmakta',
          thumbnailAlt: room?.name ?? 'Setur Görseli Hazırlanmakta',
          thumbnail:
            cloudineryImage(roomImages?.url, '137', '65') ??
            '/static/images/setur-static.jpg',
          description:
            room.name && room.typeName ? room.name + ' ' + room.typeName : '',
        })
      )
    );

    setHotelGalleries((currentValue) => ({
      Odalar: roomsCategory,

      ...currentValue,
    }));
  }, [hotelData]);
  const [viewedRoomGallery, setViewedRoomGallery] = useState<
    ReturnType<typeof groupImagesByCategory>
  >({});

  const { unlockBasketAsync, cleanBasketSignal } = useUnlockBasket({
    basketId: basketId || '',
  });

  useEffect(() => {
    setViewedRoomGallery(
      groupImagesByCategory(viewedRoom?.images ?? [], '', 'Oda Fotoğrafı')
    );
  }, [viewedRoom?.images]);
  useEffect(() => {
    setSelectedVariations([]);
  }, [selectedTab]);
  // ===============================
  // Handle add hotel to basket
  // ===============================
  const addBasket = async () => {
    const basketId =
      typeof window !== 'undefined' && localStorage.getItem('basketId');
    const hotelVariation = selectedVariations?.[0];
    const selectedHotelRoom = hotelData?.rooms.find(
      (r) => r.id === hotelVariation?.roomId
    );

    let flights: Array<FlightNodeViewModel | null> = [];
    let price = 0;
    const token = selectedDepatureFlight?.summary.token ?? '';
    let tokens: string[] = [];

    flights = [
      selectedDepatureFlight?.flights[0] ?? null,
      selectedReturnFlight?.flights[0] ?? null,
    ];
    price =
      (selectedDepatureFlight?.orginalItem?.totalPrice ?? 0) +
      (selectedReturnFlight?.orginalItem?.totalPrice ?? 0);
    tokens = [
      selectedDepatureFlight?.summary.token ?? '',
      selectedReturnFlight?.summary.token ?? '',
    ];

    const childrenBirthDates: (string | Date)[] = [];
    const childrenAges: number[] = [];

    hotelRoomList?.forEach((room) => {
      room.childrenList.forEach((age) => {
        childrenBirthDates.push(age.age);
      });
    });

    childrenBirthDates?.forEach((birthDate) => {
      childrenAges.push(
        moment(selectedDepatureFlight?.departureDate).diff(
          moment(parseDate(String(birthDate), 'dd/MM/yyyy', new Date())),
          'years'
        )
      );
    });

    const prebookAddTransferItem = prepareTransferForPrebookAdd(
      adultCount,
      childCount,
      childrenBirthDates?.map((birthDate) =>
        moment(parseDate(String(birthDate), 'dd/MM/yyyy', new Date()))?.format(
          'YYYY/MM/DD'
        )
      ) ?? [''],
      selectedDepatureTransfer,
      selectedReturnTransfer
    );

    const body: PackagePrebookAddRequestViewModel = {
      partialGuid: packageData?.partialGuid ?? '',
      shoppingFileId: packageData?.shoppingFileId ?? '',
      flight: {
        travelers: getFlightPrebookTravelersFromGuestInfo({
          adultCount,
          childCount: childrenAges.filter((age) => age >= 2).length,
          infantCount: childrenAges.filter((age) => age < 2).length,
        }),
        plan: {
          flights: cleanArray(flights),
          token,
          tokens,
          price,
          currency: 'TL',
        },
        domestic: selectedDepatureFlight?.domestic ?? false,
      },
      hotel: {
        hotelId: hotelData?.id || 0,
        provider: hotelVariation?.provider,
        roomId: hotelVariation?.roomId,
        roomName: selectedHotelRoom?.name ?? '',
        roomTypeName: selectedHotelRoom?.typeName ?? '',
        roomIndex: selectedHotelRoom
          ? hotelData?.rooms?.indexOf(selectedHotelRoom) ?? -1
          : -1,
        cancellationPolicyId: hotelVariation?.cancelletionPolicyId,
        boardTypeId: hotelVariation?.boardTypeId,
        boardTypeName: hotelVariation?.boardTypeName,
        checkInDate: formatArrayToString(startDate || ''),
        checkOutDate: formatArrayToString(endDate || ''),
        guests: {
          adultCount: hotelVariation?.guests.adultCount || 0,
          childBirthdates: hotelVariation?.guests.childrenBirthDates || [''],
        },
        variationId: hotelVariation?.variationId,
      },
      transfer: prebookAddTransferItem,
    };

    packageDetailAndAddtoCart(
      hotelData,
      startDate,
      endDate,
      query,
      selectedVariations
    );

    await unlockBasketAsync();

    const basketState = await cleanBasketSignal;

    return callService(
      { service: 'PrebookService', method: 'prebookAddPackage', version: 'v3' },
      {
        basketId: basketState === 'stale' || !basketId ? '' : basketId,
        body,
      }
    );
  };

  const prebookMutation = useMutation(['packagePrebook', push], addBasket, {
    onSuccess: ({ basketId }) => {
      localStorage.setItem('detailUrl', asPath);
      localStorage.setItem('basketId', basketId);
      setCookie('basketId', basketId);
      push(`/seyahat-planlama?bid=${basketId}`);
    },
    onError: (error: ApiError) => {
      if (error?.body?.errors?.[0]?.code === 'LOYALTY_SWITCH_ERROR') {
        const { basketId, errors } = error.body;
        sessionStorage.setItem(
          'switchError',
          JSON.stringify({
            message: errors?.[0]?.message || '',
            items: errors?.map((item) => item.errorItemId),
          })
        );
        localStorage.setItem('basketId', basketId);
        setCookie('basketId', basketId);
        setErrorModalStatusMessage({
          title: 'Uyarı',
          description: 'Loyalty promosyonu kazanmadan ürün eklenmektedir. ',
        });
        setTimeout(() => {
          push(`/seyahat-planlama?bid=${basketId}`);
        }, 1500);
      } else {
        setErrorModalStatusMessage({
          title: 'Ooops',
          description:
            error?.body?.errors?.[0]?.detail ||
            'Sepete ürün eklemeye çalışırken, genel bir hata oluştu, tekrar deneyiniz.',
        });
      }
    },
  });

  const handleConfirmRemoveCurrentCartProducts = async (): Promise<void> => {
    if (!basketMutationData) return;
    const basketItems = extractItems(basketMutationData);

    setCurrentCartProductsModal(false);

    if (basketItems?.length) {
      for (let i = 0; i < basketItems?.length; i++) {
        await callService(
          { service: 'BasketsService', method: 'remove', version: 'v3' },
          {
            basketId: basketInfo?.id || '',
            itemId: basketItems[i].itemId,
          }
        );
      }

      prebookMutation.mutate();
    } else {
      prebookMutation.mutate();
    }
  };

  // ===============================
  //Handle hotel drawer items
  // ===============================
  const drawerItems = useMemo(
    (): PriceDetailsDrawerItem[] =>
      selectedVariations?.map((variation) => {
        return {
          id: variation.variationId,
          name: variation.promotionInfoName,
          price:
            variation.totalDiscountedSellingPrice +
            (selectedDepatureFlight?.orginalItem?.totalPrice || 0) +
            (selectedReturnFlight?.orginalItem?.totalPrice || 0) +
            totalTransferPrice,
          tabNumber: variation.tabNumber,
        };
      }),
    [
      selectedVariations,
      selectedDepatureFlight,
      selectedDepatureTransfer,
      selectedReturnFlight,
      selectedReturnTransfer,
    ]
  );

  const handleRemoveVariation = (id: string): void =>
    setSelectedVariations(
      selectedVariations.filter((room) => room.variationId !== id)
    );

  const handleNavigateRooms = (): void => {
    if (hotelData?.nightCount && !hotelData?.rooms?.length) {
      roomsRef?.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    } else {
      roomsRef?.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  };
  const ClickTripAdvisor = (): void => {
    scoringRef?.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  };
  const handleNavigateAnnouncment = (): void => {
    announcementsRef?.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  };

  const handleNavigateAboutUs = (): void =>
    aboutsRef?.current?.scrollIntoView({ behavior: 'smooth' });

  const handleNavigateTravelPlanning = async (): Promise<void> => {
    const basketId =
      typeof window !== 'undefined' && localStorage.getItem('basketId');
    if (basketId) {
      basketMutate(basketId, {
        onSuccess: (data) => {
          if (!isEmptyBasket(data)) {
            setCurrentCartProductsModal(true);
          } else {
            setIsRedirecting(true);
            if (basketInfo && basketInfo.isPackageBasket) {
              setIsRedirecting(false);
              toggleConfirmModal();
            } else {
              prebookMutation.mutate();
            }
          }
        },
      });
    } else {
      setIsRedirecting(true);
      if (basketInfo && basketInfo.isPackageBasket) {
        setIsRedirecting(false);
        toggleConfirmModal();
      } else {
        prebookMutation.mutate();
      }
    }
  };

  const handleLoyaltySubmit = () => {
    refetchPackage();
    setModalLoyaltyType(null);
  };

  const toggleMap = (): void =>
    hotelData?.latitude && hotelData.longitude
      ? setShowMap((state) => !state)
      : setShowMap(false);

  // ========================================
  // Toggle Installments Modal
  // ========================================
  const toggleInstallments = (): void => setShowInstallments((state) => !state);

  // ========================================
  // Toggle Features Gallery Modal
  // ========================================
  const toggleFeaturesModal = () => {
    setActiveGalleryModalName('all');
    setStartImage(0);
    setGalleryDetailsModalViewInfo({
      galleriesDetailsModalShown: true,
      view: ModalView.GALLERIES,
      entityType: 'hotel',
    });
  };

  //TODO: typeName is not coming for package after it comes title will be defined as
  //`${viewedRoom?.name},${viewedRoom?.typeName ?? ''}`
  const detailsAndGalleriesProps = {
    galleries:
      modalViewInfo.entityType === 'hotel' ? hotelGalleries : viewedRoomGallery,
    title:
      modalViewInfo.entityType === 'hotel'
        ? hotelData?.name
        : `${viewedRoom?.name}}`,
    buttonActionMessage:
      modalViewInfo.entityType === 'hotel' ? 'Odaları Gör' : 'Oda Seç',
  };
  const scrollToBottom = () => {
    announcementsRef.current?.scrollIntoView({ behavior: 'smooth' });
  };
  const featuresProps = getTypedFeatureProps({
    entityType: modalViewInfo.entityType,
    features:
      modalViewInfo.entityType === 'hotel'
        ? hotelData?.features ?? []
        : viewedRoom?.features ?? [],
  });
  const errorInfoModalClosed = () => {
    setErrorModalStatusMessage({
      title: '',
      description: '',
    });
    prebookMutation.reset();
  };

  useEffect(() => {
    if (
      showErrorModalDeletePackage ||
      confirmPackageModal ||
      showErrorModalPackageTimer ||
      prebookMutation.isError
    ) {
      setIsRedirecting(false);
    }
  }, [
    showErrorModalDeletePackage,
    confirmPackageModal,
    prebookMutation.isError,
    showErrorModalPackageTimer,
  ]);

  useEffect(() => {
    if (!hotelData?.rooms || hotelData?.rooms.length === 0) {
      setHotelRooms([]);
      return;
    }
    const rooms: HotelRooms[] = [];
    hotelData?.rooms?.forEach((room) => {
      rooms.push({
        ...room,
        roomTypeId: generateUUID(),
      });
    });

    setHotelRooms(rooms);
  }, [hotelData]);

  const totalImages = useMemo(() => {
    return (
      (getRoomsGalleryCategory(hotelData?.rooms ?? [])?.length ?? 0) +
      (hotelData?.images?.length ?? 0)
    );
  }, [hotelData]);
  const onlyHoteltotalImages = useMemo(() => {
    return (
      (getRoomsGalleryCategoryHotel(onlyHotelData?.rooms ?? [])?.length ?? 0) +
      (onlyHotelData?.images?.length ?? 0)
    );
  }, [onlyHotelData]);

  const RoomsVariationsItems = useMemo(() => {
    const roomsVariations: RoomsVariationBarItem[] = [];

    if (!hotelRooms || hotelRooms?.length === 0) return [];

    hotelRooms?.forEach((room) => {
      const lowestPriceItem = getLowestVariation(room);
      if (lowestPriceItem) {
        roomsVariations?.push({
          roomName:
            `${room?.name} ${room?.typeName ? ` - ${room?.typeName}` : ''} ` ||
            '',
          roomId: room?.roomTypeId ?? '',
          price: lowestPriceItem?.totalDiscountedSellingPrice || 0,
          currency: lowestPriceItem.currency || Currencies.TL,
          isActive: false,
          isAvailable: getAvailableRoom(room),
        });
      } else {
        roomsVariations?.push({
          roomName:
            `${room?.name} ${room?.typeName ? ` - ${room?.typeName}` : ''} ` ||
            '',
          roomId: room?.roomTypeId ?? '',
          price: 0,
          currency: '',
          isActive: false,
          isAvailable: false,
        });
      }
    });

    return [
      ...sortRoomVariationItems(
        getOnlyAvailableRoomVariation(roomsVariations, true)
      ),
      ...sortRoomVariationItems(
        getOnlyAvailableRoomVariation(roomsVariations, false)
      ),
    ];
  }, [hotelRooms, query]);

  useEffect(() => {
    if (showErrorModalPackageTimer) setCurrentCartProductsModal(false);
  }, [showErrorModalPackageTimer]);

  useEffect(() => {
    if (hotelData?.id)
      packageDetailAndAddtoCart(hotelData, startDate, endDate, query, []);
  }, [hotelData]);

  const [startImage, setStartImage] = useState(0);

  const pid = getBasketFromCache()?.pid;

  const showHotelSkeleton = useMemo(() => {
    return isClientSide() && (isPackageLoading || !!showLoader);
  }, [isPackageLoading, isPackageFetching]);

  if (isGetPackageDetailError) return <ErrorPage />;
  const onCloseCallMeModal = () => {
    setIsLetUsCallYouModalVisible(false);
  };
  const onAlertClick = () => {
    setIsLetUsCallYouModalVisible(true);
    onLetUsCallYouFormSendSuccess();
  };
  const onLetUsCallYouFormSendSuccess = () => {
    addHotelLetUsCallYouSuccessEventToGTM({
      title: '',
      hotelName: hotelData?.name || '',
      checkInDate: formatMomentDate(inDate, GTM_DATE_FORMAT),
      checkOutDate: formatMomentDate(outDate, GTM_DATE_FORMAT),
      serviceType: 'Hotel',
    });
  };
  const tesisFeature = hotelData?.features?.find(
    (feature) => feature.name === 'Tesis Özellikleri'
  );
  const handleRemovePackageItems = async () => {
    setIsRedirecting(true);
    setDisable(true);
    try {
      if (basketInfo?.isPackageBasket && basketInfo?.id) {
        unlockBasketAsync();
        await callService(
          {
            service: 'BasketsService',
            method: 'packagesRemove',
            version: 'v3',
          },
          {
            basketId: basketInfo.id,
            itemId: basketInfo.hotels[0].itemId ?? '',
          }
        );
      }
      toggleConfirmModal();
      prebookMutation.mutate();
    } catch (error) {
      setShowErrorModalDeletePackage(true);
    }
  };
  const beginDate = startDate
    ? new Date(formatArrayToString(startDate || ''))
    : '';

  return (
    <Container>
      <ScrollToTop />
      {hotelData ? <HotelDetailSchemaOrg hotelDetail={hotelData} /> : null}

      {currentCartProductsModal ? (
        <CurrentCartProductsModal
          onClose={() => setCurrentCartProductsModal(false)}
          onConfirm={() => {
            handleConfirmRemoveCurrentCartProducts();
          }}
        />
      ) : null}
      {hotelData?.bankInstallments && showInstallments ? (
        <BankInstallments
          bankInstallments={hotelData.bankInstallments}
          onClose={toggleInstallments}
        />
      ) : null}

      {loyaltyType ? (
        <LoyaltyVerifications
          action={handleLoyaltySubmit}
          loyaltyType={loyaltyType}
          onCancel={() => setModalLoyaltyType(null)}
        />
      ) : null}

      <>
        {!isDesktop ? (
          <PackageResponsiveSeachBar
            setIsRedirecting={setIsRedirecting}
            setShowFetchingAnimation={setShowFetchingAnimation}
            detail={true}
            shouldResearchPackage={shouldResearchPackage}
            selectedTab={selectedTab}
            setSelectedTab={setSelectedTab}
            isAirportSelectionClicked={isAirportSelectionClicked}
            setIsAirportSelectionClicked={setIsAirportSelectionClicked}
            isAirportDateSelectionStarted={isAirportDateSelectionStarted}
            setisAirportDateSelectionStarted={setIsAirportDateSelectionStarted}
            setShouldResearchPackage={setShouldResearchPackage}
          />
        ) : null}
        {!isDesktop &&
        shouldResearchPackage &&
        query.dep &&
        query.in &&
        query.dep ? (
          <Text>
            Güncel paket fiyatlarını görüntüleyebilmek için lütfen yeniden arama
            yapınız.
          </Text>
        ) : null}
        {isDesktop ? (
          <BreadcrumbDesktop>
            {hotelData ? (
              <>
                {hotelData?.breadcrumbs && hotelData?.breadcrumbs?.length ? (
                  <BreadCrumbListSchemaOrg
                    data={hotelData?.breadcrumbs as Record<string, string>[]}
                  />
                ) : null}
                <Breadcrumb
                  setIsRedirecting={setIsRedirecting}
                  detail={true}
                  items={hotelData?.breadcrumbs || []}
                />
              </>
            ) : onlyHotelData ? (
              <>
                {onlyHotelData?.breadcrumbs &&
                onlyHotelData?.breadcrumbs?.length ? (
                  <BreadCrumbListSchemaOrg
                    data={
                      onlyHotelData?.breadcrumbs as Record<string, string>[]
                    }
                  />
                ) : null}
                <Breadcrumb
                  setIsRedirecting={setIsRedirecting}
                  detail={true}
                  items={onlyHotelData?.breadcrumbs || []}
                />
              </>
            ) : null}
          </BreadcrumbDesktop>
        ) : null}
        {isDesktop ? (
          <CyprusTabWrapper>
            <CyprusTab
              selectedTab={selectedTab}
              setSelectedTab={setSelectedTab}
              shouldResearchPackage={shouldResearchPackage}
            />
          </CyprusTabWrapper>
        ) : null}

        <PackageSearchBar
          setIsRedirecting={setIsRedirecting}
          selectedTab={selectedTab === 'package' ? true : false}
          shouldResearchPackage={shouldResearchPackage}
          data={hotelData}
          withPackageTab={true}
          onResearch={() => {
            setSelectedVariations([]);
            setShowFetchingAnimation(true);
            setShouldResearchPackage(false);
          }}
          refetchPackage={refetchPackage}
          isAirportSelectionClicked={isAirportSelectionClicked}
          setIsAirportSelectionClicked={setIsAirportSelectionClicked}
          isAirportDateSelectionStarted={isAirportDateSelectionStarted}
          setisAirportDateSelectionStarted={setIsAirportDateSelectionStarted}
        />
      </>
      {showMap && hotelData?.latitude && hotelData?.longitude ? (
        <Modal
          width={isDesktop ? 960 : '100vw'}
          height={isDesktop ? '550px' : '98vh'}
          padding='0px 10px 10px 10px'
          onCancel={toggleMap}
          borderRadius={8}
          modalBodyHeight='100vh'
        >
          <Map latitude={hotelData.latitude} longitude={hotelData.longitude} />
        </Modal>
      ) : showMap && onlyHotelData?.latitude && onlyHotelData?.longitude ? (
        <Modal
          width={isDesktop ? 960 : '100vw'}
          height={isDesktop ? '550px' : '98vh'}
          padding='0px 10px 10px 10px'
          onCancel={toggleMap}
        >
          <Map
            latitude={onlyHotelData.latitude}
            longitude={onlyHotelData.longitude}
          />
        </Modal>
      ) : null}
      {isMobile && hotelData?.isExpedia ? (
        <BreadcrumbWrapper>
          <Breadcrumb
            setIsRedirecting={setIsRedirecting}
            items={hotelData?.breadcrumbs || []}
          />
        </BreadcrumbWrapper>
      ) : isMobile && onlyHotelData?.isExpedia ? (
        <BreadcrumbWrapper>
          <Breadcrumb
            setIsRedirecting={setIsRedirecting}
            items={hotelData?.breadcrumbs || []}
          />
        </BreadcrumbWrapper>
      ) : null}

      {showHotelSkeleton ? (
        <HotelDetailSkeleton />
      ) : (
        <>
          <Element name='overview' className='element'>
            <ImagesWrapper>
              {hotelData ? (
                <GalleryHeroImages
                  isSeturExecutive={hotelData?.seturExecutive}
                  isSafe={hotelData?.secure}
                  isSuggested={hotelData?.suggests}
                  images={formatHotelImages(
                    hotelData?.images?.slice(0, 3),
                    hotelData?.name
                  )}
                  totalImages={totalImages}
                  viewGallery={toggleFeaturesModal}
                />
              ) : (
                <GalleryHeroImages
                  isSeturExecutive={onlyHotelData?.seturExecutive}
                  isSafe={onlyHotelData?.secure}
                  isSuggested={onlyHotelData?.suggests}
                  images={formatHotelImages(
                    onlyHotelData?.images?.slice(0, 3),
                    onlyHotelData?.name
                  )}
                  totalImages={onlyHoteltotalImages}
                  viewGallery={toggleFeaturesModal}
                />
              )}
            </ImagesWrapper>
          </Element>
          <Layout>
            <Wrapper>
              <Element name='overview' className='element'>
                <PackageSummaryCard
                  data={hotelData}
                  shouldResearchPackage={shouldResearchPackage}
                  onlyHotelData={onlyHotelData}
                  flightData={flightData}
                  tesisFeature={tesisFeature}
                  transferData={transferData}
                  updatedLowestPrice={updatedLowestPrice}
                  userInfo={userInfo}
                  handleNavigateRooms={handleNavigateRooms}
                  ClickTripAdvisor={ClickTripAdvisor}
                  toggleMap={toggleMap}
                  handleNavigateAboutUs={handleNavigateAboutUs}
                  room={room}
                  startDate={startDate}
                  endDate={endDate}
                  showKocAilem={showKocAilem}
                  toggleInstallments={toggleInstallments}
                  timeRemaining={
                    packageData?.shoppingFileId
                      ? timers.find(
                          (timer) => timer.id === packageData.shoppingFileId
                        )?.timeRemaining
                      : ''
                  }
                />
              </Element>
              {hotelData && hotelData?.announcements?.length > 0 ? (
                <>
                  {hotelData.banner?.images ||
                  hotelData.banner?.responsiveImages ||
                  hotelData.banner?.announcements ? (
                    <>
                      <Separator />
                      <Element name='announcements' className='element'>
                        {hotelData.banner?.announcements.length > 0 ? (
                          <BannerDetailCarousel
                            data={hotelData.banner.announcements}
                            onClick={() => scrollToBottom()}
                          />
                        ) : (
                          <>
                            {isDesktop && hotelData.banner ? (
                              <>
                                {hotelData?.banner?.images?.[0]?.url ? (
                                  <BannerDetailWrapper
                                    onClick={handleNavigateAnnouncment}
                                  >
                                    <BannerDetail
                                      url={hotelData.banner?.images[0]?.url}
                                      alt={hotelData.banner?.images[0]?.alt}
                                    />
                                  </BannerDetailWrapper>
                                ) : null}
                              </>
                            ) : (
                              <>
                                {hotelData.banner.responsiveImages[0]?.url ? (
                                  <BannerDetailWrapper
                                    onClick={handleNavigateAnnouncment}
                                  >
                                    <BannerDetail
                                      url={
                                        hotelData.banner?.responsiveImages[0]
                                          ?.url
                                      }
                                      alt={
                                        hotelData.banner?.responsiveImages[0]
                                          ?.alt
                                      }
                                    />
                                  </BannerDetailWrapper>
                                ) : null}
                              </>
                            )}
                          </>
                        )}
                      </Element>
                    </>
                  ) : null}
                </>
              ) : null}

              <Separator />
              <Element name='rooms' className='element'>
                <div ref={roomsRef as MutableRefObject<HTMLDivElement>}>
                  {shouldResearchPackage &&
                  query.in &&
                  query.out &&
                  query.dep ? (
                    <ResearchContainer>
                      <ResearchWrapper>
                        <CustomIcon
                          icon={InfoIcon}
                          width={40}
                          height={40}
                          paletteColor='Blue600'
                        />
                        <Typography
                          variant='caption1'
                          weight='Regular'
                          color='Blue600'
                        >
                          Güncel paket fiyatlarını görüntüleyebilmek için lütfen
                          yeniden arama yapınız.
                        </Typography>
                      </ResearchWrapper>
                    </ResearchContainer>
                  ) : null}
                  {!shouldResearchPackage &&
                  hotelData?.nightCount &&
                  hotelData?.rooms?.length &&
                  flightData &&
                  transferData ? (
                    <FlightTransferSelectionComponent
                      adultCount={
                        hotelData?.rooms?.[0].informations[0].adultCount || 0
                      }
                      childCount={
                        hotelData?.rooms?.[0].informations[0].childCount || 0
                      }
                      transferItems={transferData}
                      flightItems={flightData}
                      selectedDepatureFlight={selectedDepatureFlight}
                      selectedReturnFlight={selectedReturnFlight}
                      selectedDepatureTransfer={selectedDepatureTransfer}
                      selectedReturnTransfer={selectedReturnTransfer}
                      setSelectedDepatureFlight={setSelectedDepatureFlight}
                      setSelectedDepatureTransfer={setSelectedDepatureTransfer}
                      setSelectedReturnFlight={setSelectedReturnFlight}
                      setSelectedReturnTransfer={setSelectedReturnTransfer}
                    />
                  ) : null}

                  {!shouldResearchPackage ||
                  !query.in ||
                  !query.out ||
                  !query.dep ? (
                    (hotelData?.nightCount && !hotelData?.rooms?.length) ||
                    (hotelData && (!transferData || !flightData)) ? (
                      query.in && query.out && query.dep ? (
                        <RoomWarningInfo
                          onAlertClick={onAlertClick}
                          onCloseCallMeModal={onCloseCallMeModal}
                          onLetUsCallYouFormSendSuccess={
                            onLetUsCallYouFormSendSuccess
                          }
                          inDate={inDate}
                          outDate={outDate}
                          data={hotelData}
                          hotelRoomList={hotelRoomList}
                          isLetUsCallYouModalVisible={
                            isLetUsCallYouModalVisible
                          }
                        />
                      ) : (
                        <RoomOptions
                          hotelId={hotelData?.id || 0}
                          hotelName={hotelData?.name || ''}
                          items={hotelRooms ?? []}
                          nightCount={hotelData?.nightCount || 1}
                          roomNumber={isArray(room) ? room.length : 1}
                          viewRoom={viewRoom}
                          isExpedia={hotelData?.isExpedia || false}
                          openDatePicker={toggleDateSelectionModal}
                          roomsVariationsItems={RoomsVariationsItems}
                          selectedTabPackage={selectedTab}
                          selectedDepatureFlight={selectedDepatureFlight}
                          selectedReturnFlight={selectedReturnFlight}
                          selectedDepatureTransfer={selectedDepatureTransfer}
                          selectedReturnTransfer={selectedReturnTransfer}
                          adultCount={adultCount}
                          childCount={childCount}
                          country={hotelData?.country}
                        />
                      )
                    ) : (
                      <RoomOptions
                        hotelId={hotelData?.id || 0}
                        hotelName={hotelData?.name || ''}
                        items={hotelRooms ?? []}
                        nightCount={hotelData?.nightCount || 1}
                        roomNumber={isArray(room) ? room.length : 1}
                        viewRoom={viewRoom}
                        isExpedia={hotelData?.isExpedia || false}
                        openDatePicker={toggleDateSelectionModal}
                        roomsVariationsItems={RoomsVariationsItems}
                        selectedTabPackage={selectedTab}
                        selectedDepatureFlight={selectedDepatureFlight}
                        selectedReturnFlight={selectedReturnFlight}
                        selectedDepatureTransfer={selectedDepatureTransfer}
                        selectedReturnTransfer={selectedReturnTransfer}
                        adultCount={adultCount}
                        childCount={childCount}
                        country={hotelData?.country}
                      />
                    )
                  ) : null}
                </div>
              </Element>
              <Separator />
              <Element name='features' className='element'>
                {hotelData && hotelData?.seasonFeatures?.length > 0 ? (
                  <div ref={featuresRef as MutableRefObject<HTMLDivElement>}>
                    <HotelFeatures
                      hotelFeatures={hotelData.seasonFeatures}
                      generalConditions={hotelData.generalConditions}
                    />
                  </div>
                ) : onlyHotelData &&
                  onlyHotelData?.seasonFeatures?.length &&
                  !shouldResearchPackage ? (
                  <div ref={featuresRef as MutableRefObject<HTMLDivElement>}>
                    <HotelFeatures
                      hotelFeatures={onlyHotelData.seasonFeatures}
                      beginDate={beginDate}
                      generalConditions={onlyHotelData.generalConditions}
                    />
                  </div>
                ) : null}
              </Element>

              <Separator />

              {hotelData?.checkinBeginTime ? (
                <Element name='rules' className='element'>
                  <Rules
                    checkinBeginTime={hotelData.checkinBeginTime}
                    checkinEndTime={hotelData.checkinEndTime}
                    mandatoryFee={hotelData.mandatoryFee}
                    optionalFee={hotelData.optionalFee}
                    checkoutTime={hotelData.checkoutTime}
                    checkinInstruction={hotelData.checkinInstruction}
                    checkinSpecialInstruction={
                      hotelData.checkinSpecialInstruction
                    }
                  />
                </Element>
              ) : null}

              <Separator />
              <Element name='about' className='element'>
                {hotelData?.description ? (
                  <div ref={aboutsRef as MutableRefObject<HTMLDivElement>}>
                    <AboutUs description={hotelData?.description} />
                  </div>
                ) : null}
              </Element>

              <Separator />
              <Element name='announcements' className='element'>
                {hotelData && hotelData?.announcements?.length > 0 ? (
                  <div
                    ref={announcementsRef as MutableRefObject<HTMLDivElement>}
                  >
                    <Announcements data={hotelData?.announcements} />
                  </div>
                ) : null}
              </Element>
              <Element name='location' className='element'>
                {hotelData?.latitude && hotelData?.longitude ? (
                  <HotelLocationWrapper>
                    <HotelLocation
                      locationName={hotelData.name}
                      locationAddress={hotelData.address}
                      latitude={hotelData.latitude}
                      longitude={hotelData.longitude}
                    />
                  </HotelLocationWrapper>
                ) : null}
              </Element>
              <Separator />
            </Wrapper>
          </Layout>
          {hotelData?.metaContent ? (
            <SeoTextContainer>
              <SeoText text={hotelData?.metaContent || ''} />
            </SeoTextContainer>
          ) : null}
          {selectedVariations.length ? (
            <PriceDetails
              isLoading={prebookMutation.isLoading}
              items={drawerItems}
              isPackage={true}
              onNavigate={handleNavigateTravelPlanning}
              onRemove={handleRemoveVariation}
              currency={hotelData?.lowestPrice?.currencyCode}
              warningMessage={
                unSelectedRooms?.length
                  ? `Devam etmek için ${unSelectedRooms[0]}. odayı seçiniz`
                  : undefined
              }
            />
          ) : null}

          {modalViewInfo.galleriesDetailsModalShown ? (
            <DetailsAndGalleries
              {...detailsAndGalleriesProps}
              {...featuresProps}
              closeModal={() => {
                setGalleryDetailsModalViewInfo({
                  galleriesDetailsModalShown: false,
                  view: ModalView.NONE,
                  entityType: 'hotel' || 'room',
                });
                setStartImage(0);
                setViewedRoom(undefined);
              }}
              price={hotelData?.lowestPrice}
              isOpen={modalViewInfo.galleriesDetailsModalShown}
              view={modalViewInfo.view}
              room={viewedRoom}
              announcements={hotelData?.announcements}
              activeGalleryModalName={activeGalleryModalName}
              startImage={startImage}
            />
          ) : null}
        </>
      )}
      {prebookMutation.isLoading || isRedirecting || showLoader ? (
        <GeneralLoader />
      ) : null}

      {prebookMutation.isError ? (
        <ErrorInfoModal
          title={errorModalStatusMessage.title}
          description={
            PAYMENT_STARTED === errorModalStatusMessage.description ? (
              <PaymentStartedErrorContainer>
                Ödeme başladığı için sepete ürün eklenemez, mevcut ödeme
                sayfasına gitmek için
                <LinkText href={`/odeme/${pid}`} prefetch={false}>
                  {' '}
                  buraya{' '}
                </LinkText>
                tıklayın
              </PaymentStartedErrorContainer>
            ) : (
              <BasketErrorText>
                {errorModalStatusMessage.description}
              </BasketErrorText>
            )
          }
          onCancel={errorInfoModalClosed}
        />
      ) : null}
      {confirmPackageModal ? (
        <ConfirmationModal
          disable={disable}
          onCancel={() => setConfirmModal(false)}
          onSubmit={handleRemovePackageItems}
          content='Sepetinizde bir Kıbrıs paket ürünü bulunmakta, devam etmeniz halinde sepetiniz silenecektir. Devam etmek ister misiniz?'
          title='Sepeti Sil'
        />
      ) : null}
      {showErrorModalDeletePackage ? (
        <ErrorModal
          onCancel={() => setShowErrorModalDeletePackage(false)}
          message='Ürün sepetten silinirken bir hata oluştu.'
        />
      ) : null}
      {showErrorModalPackageTimer ? (
        <ErrorModal
          width={isDesktop ? 400 : 350}
          padding='20px'
          closable={false}
          hideCloseIcon={true}
          showLabel={false}
          iconSize={60}
          isPhoneFullHeight={false}
          showContentPaddingTop={false}
        >
          <ErrorModalBody>
            <Typography variant='caption1' weight='Regular' color='D500'>
              Rezervasyon süresi doldu. Otel detay sayfasına dönerek tekrar
              rezervasyon yapabilirsiniz.
            </Typography>
            <ErrorModalButton onClick={() => backToDetail()}>
              <Typography variant='caption2' weight='Bold' color='D400'>
                Detaya Dön
              </Typography>
            </ErrorModalButton>
          </ErrorModalBody>
        </ErrorModal>
      ) : null}
    </Container>
  );
};

export default PackageDetail;
